import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/loop";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Header";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function AuthPage() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [role, setRole] = useState("buyer");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const dash = (u) => (u.role === "admin" ? "/admin" : u.role === "owner" ? "/owner" : "/buyer");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = mode === "login"
        ? await login(form.email, form.password)
        : await register({ ...form, role });
      toast.success(`Welcome${u.name ? ", " + u.name.split(" ")[0] : ""}`);
      nav(dash(u));
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const fill = (email, password) => setForm({ ...form, email, password });

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16 watermark-grid">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><Logo /></div>
        <div className="bg-[#0B132B] border border-white/10 rounded-3xl p-8 shadow-2xl">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#C5A059] font-semibold mb-1">
            <ShieldCheck className="w-4 h-4" /> Real Estate, Verified
          </div>
          <h1 className="text-2xl font-bold text-white mb-6">
            {mode === "login" ? "Sign in to LOOP" : "Create your account"}
          </h1>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <Label className="text-slate-300">Full name</Label>
                  <Input data-testid="auth-name-input" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1.5 bg-[#131B35] border-white/10 text-white" placeholder="Your name" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {["buyer", "owner"].map((r) => (
                    <button key={r} type="button" data-testid={`auth-role-${r}`} onClick={() => setRole(r)}
                      className={`py-2 rounded-lg text-sm font-medium border capitalize transition-colors
                        ${role === r ? "bg-[#C5A059] text-[#070C1E] border-[#C5A059]"
                                     : "bg-transparent text-slate-300 border-white/15"}`}>
                      {r === "owner" ? "List property (Owner)" : "Buy / Rent (Buyer)"}
                    </button>
                  ))}
                </div>
              </>
            )}
            <div>
              <Label className="text-slate-300">Email</Label>
              <Input data-testid="auth-email-input" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 bg-[#131B35] border-white/10 text-white" placeholder="you@email.com" />
            </div>
            <div>
              <Label className="text-slate-300">Password</Label>
              <Input data-testid="auth-password-input" type="password" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 bg-[#131B35] border-white/10 text-white" placeholder="••••••••" />
            </div>

            {err && <p data-testid="auth-error" className="text-sm text-rose-400">{err}</p>}

            <Button data-testid="auth-submit-btn" type="submit" disabled={loading}
              className="w-full bg-[#C5A059] hover:bg-[#b18f47] text-[#070C1E] font-semibold">
              {loading ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
            </Button>
          </form>

          <button data-testid="auth-toggle-mode"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}
            className="mt-5 w-full text-center text-sm text-slate-400 hover:text-white">
            {mode === "login" ? "New to LOOP? Create an account" : "Already have an account? Sign in"}
          </button>

          <div className="mt-6 pt-5 border-t border-white/10">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Demo accounts</p>
            <div className="space-y-1.5 text-xs text-slate-400">
              <button data-testid="demo-buyer" onClick={() => fill("buyer@loop.com", "Buyer@2026")} className="block hover:text-[#C5A059]">Buyer → buyer@loop.com / Buyer@2026</button>
              <button data-testid="demo-owner" onClick={() => fill("owner@loop.com", "Owner@2026")} className="block hover:text-[#C5A059]">Owner → owner@loop.com / Owner@2026</button>
              <button data-testid="demo-admin" onClick={() => fill("sunilpupun59@gmail.com", "LoopAdmin@2026")} className="block hover:text-[#C5A059]">Admin → sunilpupun59@gmail.com / LoopAdmin@2026</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
